import React, { FC, Ref, SVGProps, useMemo, useState } from 'react';
import { Box, Collapse, IconButton, MenuItem, Tooltip, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useDrag, useDrop } from 'react-dnd';
import { EventPriceTypesEnum, MembershipDto, priceFormatter } from '@sweatpals/shared';
import { isNumber, map } from 'lodash-es';
import {
	CheckIcon,
	DollarIcon,
	DragIcon,
	GroupTierIcon,
	InfoIcon,
	TextIcon,
	TicketIcon,
	TicketsIcon,
	TicketsOutlined,
	VisibilityOffIcon,
	SpotsIcon,
} from '../../../icons';
import { Select, TextField } from '../../inputs';
import { PricesFormDto } from '../../../models/dto';
import { useCustomRegister } from '../../../hooks';
import { CoreLabeledCheckbox, CoreNumberInput } from '../../_core';
import { Card } from '../../card';
import { CollapseTriggerWithIcon } from '../../collapse-trigger-with-icon/collapse-trigger-with-icon.component';
import { Colors } from '../../../constants';
import { EventPriceToMembership } from './event-price-to-membership.component';
import { Showable } from '../../utils';
import { clsx, extendPriceToMembershipListWithMissingMemberships } from '../../../utils';
import { checkBoxStyles, checkBoxLabelStyles, dragAndDropIconStyles } from './prices.styles';

export const DragAndDropPriceCardType = 'price-card';

interface DragItem {
	id: string;
	index: number;
}

export interface PriceCardProps {
	id: string;
	index: number;
	nonUniqueTiers: string[];
	communityMemberships?: MembershipDto[];
	canDrag?: boolean;
	disabled?: boolean;
	onRemove(): void;
	onMove(index: number, nexIndex: number): void;
	totalTicketsCount?: number;
}

type TierTypesOption = {
	Icon: FC<SVGProps<SVGSVGElement>>;
	title: string;
	description: string;
};

export const PriceCard: FC<PriceCardProps> = props => {
	const { id, canDrag, disabled, index, nonUniqueTiers, communityMemberships, onRemove, onMove, totalTicketsCount } =
		props;

	const form = useFormContext<PricesFormDto>();
	const { control, setValue, watch, formState, trigger } = form;
	const { register } = useCustomRegister(form);

	const data = watch(`tiers.${index}`);
	const errors = useMemo(() => formState.errors?.tiers?.[index] ?? {}, [formState.errors?.tiers, index]);

	const { fields: pricesToMemberships } = useFieldArray({
		name: `tiers.${index}.eventPricesToMemberships`,
		control,
	});

	const [{ opacity }, drag, preview] = useDrag(
		() => ({
			type: DragAndDropPriceCardType,
			item: { id, index },
			canDrag: canDrag && !disabled,
			collect: monitor => ({
				opacity: monitor.isDragging() ? 0 : 1,
			}),
		}),
		[id, index]
	);
	const [, drop] = useDrop<DragItem>(
		() => ({
			accept: DragAndDropPriceCardType,
			hover: item => {
				const { id: draggedId, index: draggedIndex } = item;

				if (draggedId !== id) {
					onMove(draggedIndex, index);
					item.index = index;
				}
			},
		}),
		[onMove, id, index]
	);

	const [advancedSettingsIsVisible, setAdvancedSettingsIsVisible] = useState(false);

	const tierTypesOptions = useMemo(
		() => ({
			[EventPriceTypesEnum.Standard]: {
				Icon: TicketsIcon,
				title: 'Standard Tier',
				description: 'A basic tier with core functions',
			},
			[EventPriceTypesEnum.Group]: {
				Icon: GroupTierIcon,
				title: 'Group Tier',
				description: 'Provide special offer for purchasing multiple tickets',
			},
			[EventPriceTypesEnum.Unlisted]: {
				Icon: VisibilityOffIcon,
				title: 'Unlisted Tier',
				description: 'Hide tier publicly, but allow to access via link',
			},
		}),
		[]
	);

	return (
		<Card
			onDelete={onRemove}
			sx={{
				p: '0',
				gap: '0',
				opacity,
			}}
			ref={(node: HTMLDivElement) => { drop(preview(node)) }}
		>
			<Box p="24px" display="flex" flexDirection="column" rowGap="10px">
				<Showable show={canDrag}>
					<Box ref={drag as unknown as Ref<unknown>} sx={clsx(dragAndDropIconStyles, { cursor: canDrag && !disabled ? 'pointer' : 'default' })}>
						<DragIcon />
					</Box>
				</Showable>

				<Box display="flex" alignItems="flex-start" gap="10px">
					<TextField
						{...register(`tiers.${index}.tierName`, {
							error: nonUniqueTiers.includes(data.tierName) || !!errors.tierName,
							helperText: nonUniqueTiers.includes(data.tierName) ? "Tier name isn't unique" : errors.tierName?.message,
						})}
						controlSx={{ width: '50%' }}
						label='Tier Name'
						labelSx={{
							fontSize: '15px',
							fontWeight: 600,
							lineHeight: '21px',
							mb: '2px',
						}}
						onBlur={() => {
							trigger(`tiers.${index}.tierName`);
							trigger(`tiers`);
						}}
						placeholder={index === 0 ? 'General Admission' : 'VIP'}
						startAdornment={<TicketIcon />}
					/>

					<CoreNumberInput
						{...(register(`tiers.${index}.priceAmount`, {
							type: 'number',
							passType: false,
							passRef: false,
							transformValue: (value: number) => value && value / 100,
						}) as any)}
						label='Price per ticket'
						labelSx={{ mb: '2px' }}
						onChange={value => {
							const newValue = value && value > 0 ? Math.round(value * 100) : 0;
							setValue(`tiers.${index}.priceAmount`, newValue);
						}}
						decimalScale={2}
						allowNegative={false}
						lang='en-US'
						placeholder='Price'
						startAdornment={<DollarIcon />}
						controlSx={{
							flex: 1,
						}}
					/>

					<Select
						{...register(`tiers.${index}.tierTypeId`)}
						label='Tier Type'
						labelSx={{ mb: '2px' }}
						controlSx={{ width: '162px' }}
						SelectDisplayProps={{
							style: {
								display: 'block',
								paddingRight: '10px',
							},
						}}
						MenuProps={{
							anchorOrigin: {
								vertical: 'bottom',
								horizontal: 'right',
							},
							transformOrigin: {
								vertical: 'top',
								horizontal: 'right',
							},
							PaperProps: {
								sx: {
									width: '349px',

									'& .MuiList-root': {
										p: 0,
									},
								},
							},
						}}
						renderValue={id => tierTypesOptions[id as EventPriceTypesEnum]?.title}
						onChange={e => {
							const newTierTypeId = e.target.value as EventPriceTypesEnum;
							setValue(`tiers.${index}.tierTypeId`, newTierTypeId);

							if (newTierTypeId !== EventPriceTypesEnum.Group) {
								setValue(`tiers.${index}.withTicketsLimitPerOrder`, false);
								setValue(`tiers.${index}.minTicketsPerOrder`, null);
								setValue(`tiers.${index}.maxTicketsPerOrder`, null);

								if (errors.minTicketsPerOrder || errors.maxTicketsPerOrder) {
									trigger(`tiers.${index}.minTicketsPerOrder`);
									trigger(`tiers.${index}.maxTicketsPerOrder`);
								}
							} else {
								setValue(`tiers.${index}.ticketsPerGroup`, null);

								if (errors.ticketsPerGroup) {
									trigger(`tiers.${index}.ticketsPerGroup`);
								}
							}
						}}
						fullWidth
					>
						{map(tierTypesOptions, ({ Icon, description, title }, tierTypeId) => (
							<MenuItem
								key={tierTypeId}
								value={tierTypeId}
								sx={{
									gap: '10px',
									p: '10px 16px',

									'& .Mui-selected': {
										backgroundColor: 'transparent',
									},
								}}
							>
								<Icon style={{ width: '24px', height: '24px', color: Colors.white }} />
								<Box flex={1} display={'flex'} overflow={'hidden'} flexDirection={'column'} gap={'2px'}>
									<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={'10px'}>
										<Typography sx={{ fontSize: '16px', fontWeight: 700, color: Colors.white }}>{title}</Typography>
										<Showable show={tierTypeId === data.tierTypeId}>
											<CheckIcon style={{ color: Colors.white, width: '14px', height: '14px' }} />
										</Showable>
									</Box>
									<Typography sx={{ fontSize: '14px', fontWeight: 500, color: Colors.white_a80, whiteSpace: 'normal' }}>
										{description}
									</Typography>
								</Box>
							</MenuItem>
						))}
					</Select>
				</Box>

				<Showable show={data.tierTypeId === EventPriceTypesEnum.Group}>
					<CoreNumberInput
						value={data.ticketsPerGroup}
						error={!!errors?.ticketsPerGroup}
						decimalScale={0}
						allowNegative={false}
						label='Tickets included per group'
						lang='en-US'
						placeholder='0'
						onChange={value => {
							setValue(`tiers.${index}.ticketsPerGroup`, value ?? null);
						}}
						onBlur={() => {
							if (errors?.ticketsPerGroup) {
								trigger(`tiers.${index}.ticketsPerGroup`);
							}
						}}
						startAdornment={<SpotsIcon />}
						sx={{
							'& .MuiInputBase-input': {
								flex: 1,
							}
						}}
						endAdornment={isNumber(data.priceAmount) && isNumber(data.ticketsPerGroup) ? (
							<Typography sx={{ fontSize: '14px', fontWeight: 500, color: Colors.white_a60 }}>
								Price for the group: {priceFormatter(data.priceAmount * data.ticketsPerGroup)}
							</Typography>
						) : null}
					/>
				</Showable>

				<TextField
					{...register(`tiers.${index}.tierDescription`)}
					fullWidth
					placeholder='Add Description'
					startAdornment={<TextIcon />}
				/>

				<Box pt="2px">
					<CollapseTriggerWithIcon
						isActivated={advancedSettingsIsVisible}
						setIsActivated={setAdvancedSettingsIsVisible}
					/>
				</Box>

				<Collapse in={advancedSettingsIsVisible}>
					<Box display={'flex'} flexDirection={'column'} gap={'14px'}>
						<Box display='flex' gap='12px'>
							<CoreLabeledCheckbox
								value={data.withMaxTickets}
								onChange={(_, checked) => {
									setValue(`tiers.${index}.withMaxTickets`, checked);

									if (!checked) {
										setValue(`tiers.${index}.maxTickets`, null);
									}
								}}
								label='Tier Capacity'
								labelSx={checkBoxLabelStyles}
								sx={checkBoxStyles}
							/>
							<Showable show={data.withMaxTickets}>
								<CoreNumberInput
									{...(register(`tiers.${index}.maxTickets`, {
										type: 'number',
										passRef: false,
										passType: false,
									}) as any)}
									onChange={value => {
										setValue(`tiers.${index}.maxTickets`, value ?? null);
									}}
									controlSx={{ flex: 1 }}
									decimalScale={0}
									allowNegative={false}
									lang='en-US'
									placeholder='Unlimited'
									startAdornment={<TicketsIcon />}
								/>
							</Showable>
						</Box>
						<Showable show={data.tierTypeId !== EventPriceTypesEnum.Group}>
							<Box display='flex' gap='12px'>
								<CoreLabeledCheckbox
									value={data.withTicketsLimitPerOrder}
									onChange={(_, checked) => {
										setValue(`tiers.${index}.withTicketsLimitPerOrder`, checked);

										if (!checked) {
											setValue(`tiers.${index}.maxTicketsPerOrder`, null);
											setValue(`tiers.${index}.minTicketsPerOrder`, null);
										}

										if (errors.maxTicketsPerOrder) {
											trigger(`tiers.${index}.maxTicketsPerOrder`)
										}

										if (errors.minTicketsPerOrder) {
											trigger(`tiers.${index}.minTicketsPerOrder`)
										}
									}}
									label='Tickets per order'
									labelSx={checkBoxLabelStyles}
									sx={checkBoxStyles}
								/>
								<Showable show={data.withTicketsLimitPerOrder}>
									<Box display="flex" alignItems="center" gap="10px" flex={1}>
										<CoreNumberInput
											value={data.minTicketsPerOrder}
											error={!!errors?.minTicketsPerOrder}
											decimalScale={0}
											allowNegative={false}
											lang='en-US'
											placeholder='0'
											onChange={value => {
												setValue(`tiers.${index}.minTicketsPerOrder`, value ?? null);
											}}
											onBlur={() => {
												if (errors?.minTicketsPerOrder) {
													trigger(`tiers.${index}.minTicketsPerOrder`);
												}
											}}
											startAdornment={<TicketsOutlined />}
											controlSx={{ flex: 1 }}
											endAdornment={
												<Typography sx={{ fontSize: '14px', fontWeight: 500, color: Colors.white_a60 }}>Min</Typography>
											}
										/>
										<Typography sx={{ fontSize: '14px', fontWeight: 600 }}>—</Typography>
										<CoreNumberInput
											value={data.maxTicketsPerOrder}
											error={!!errors?.maxTicketsPerOrder}
											decimalScale={0}
											allowNegative={false}
											lang='en-US'
											placeholder='0'
											onChange={value => {
												setValue(`tiers.${index}.maxTicketsPerOrder`, value ?? null);
											}}
											onBlur={() => {
												if (errors?.maxTicketsPerOrder) {
													trigger(`tiers.${index}.maxTicketsPerOrder`);
												}
											}}
											startAdornment={<TicketsOutlined />}
											controlSx={{ flex: 1 }}
											endAdornment={
												<Typography sx={{ fontSize: '14px', fontWeight: 500, color: Colors.white_a60 }}>Max</Typography>
											}
										/>
									</Box>
								</Showable>
							</Box>
						</Showable>
						<Showable show={!!communityMemberships?.length}>
							<CoreLabeledCheckbox
								value={data.withMemberships}
								onChange={(_, checked) => {
									setValue(`tiers.${index}.withMemberships`, checked);

									setValue(
										`tiers.${index}.eventPricesToMemberships`,
										checked ? extendPriceToMembershipListWithMissingMemberships([], communityMemberships) : []
									);
								}}
								label='Memberships Policy'
								labelSx={checkBoxLabelStyles}
								sx={checkBoxStyles}
							/>
						</Showable>
						{pricesToMemberships?.map((membership, membershipIndex) => (
							<EventPriceToMembership
								key={membership.id}
								membershipIndex={membershipIndex}
								tierIndex={index}
								communityMemberships={communityMemberships}
							/>
						))}
					</Box>
				</Collapse>
			</Box>
			<Showable show={isNumber(totalTicketsCount)}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '10px',
						padding: '8px',
						backgroundColor: Colors.white_a5,
						borderTop: `1px solid ${Colors.white_a5}`,
					}}
				>
					<Typography
						sx={{
							fontSize: '10px',
							fontWeight: 500,
							color: Colors.white,
							lineHeight: '12px',
							textTransform: 'uppercase',
							fontFamily: 'Montserrat',
						}}
					>
						Tickets sold
					</Typography>
					<Typography
						sx={{
							fontSize: '13px',
							fontWeight: 600,
							color: data.maxTickets && totalTicketsCount! > data.maxTickets ? '#F13939' : Colors.white,
							lineHeight: '21px',
							textTransform: 'uppercase',
							fontFamily: 'Montserrat',
						}}
					>
						{totalTicketsCount}
						<Box
							component={'span'}
							sx={{
								fontSize: '13px',
								fontWeight: 600,
								color: Colors.white_a70,
								lineHeight: '21px',
								textTransform: 'uppercase',
								fontFamily: 'Montserrat',
							}}
						>
							{' '}
							/ {data.maxTickets ?? '∞'}
						</Box>
					</Typography>
					<Showable show={data.maxTickets && totalTicketsCount! > data.maxTickets}>
						<Tooltip
							title='Because of your earlier tier capacity limit, tickets were oversold'
							placement='top'
							componentsProps={{
								popper: {
									sx: {
										zIndex: 10000,
									},
								},
								tooltip: {
									sx: {
										bgcolor: '#222222',
										color: '#fff',
										fontSize: '14px',
										fontFamily: 'Montserrat',
										padding: '12px',
										boxSizing: 'border-box',
										width: 260,
										border: '1px solid #343434',
										borderBottom: 'none',
										borderRadius: '8px',
										textAlign: 'center',
									},
								},
							}}
							arrow
						>
							<IconButton sx={{ '&:hover': { backgroundColor: 'transparent' }, padding: '0' }}>
								<InfoIcon />
							</IconButton>
						</Tooltip>
					</Showable>
				</Box>
			</Showable>
		</Card>
	);
};
